import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { collection, addDoc, setDoc, serverTimestamp, doc } from 'firebase/firestore';
import { Container, Form, Button, Alert, Spinner, Card, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const NewsUploadPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'news',
    author: 'مؤسسة إرضاء الإعلامية',
    excerpt: '',
    tags: ''
  });
  const [newsImages, setNewsImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [isArabic, setIsArabic] = useState(true);
  const navigate = useNavigate();

  // Load custom Arabic fonts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Black.ttf') format('truetype');
        font-weight: 900;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Medium.ttf') format('truetype');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Regular.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
      @font-face {
        font-family: 'ITF Qomra Arabic';
        src: url('/fonts/itfQomraArabic-Light.ttf') format('truetype');
        font-weight: 300;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);
  }, []);

  const translations = {
    'News Upload': 'رفع خبر',
    'Publish your news article': 'انشر مقالك الإخباري',
    'Title *': 'العنوان *',
    'Enter news title': 'أدخل عنوان الخبر',
    'Content *': 'المحتوى *',
    'Write your news content': 'اكتب محتوى الخبر',
    'Category *': 'الفئة *',
    'Select a category': 'اختر فئة',
    'Author *': 'المؤلف *',
    'Enter author name': 'أدخل اسم المؤلف',
    'Excerpt': 'ملخص',
    'Brief description of the news': 'وصف مختصر للخبر',
    'Tags': 'الكلمات الدلالية',
    'Comma-separated tags': 'كلمات دلالية مفصولة بفواصل',
    'Featured Image': 'الصورة الرئيسية',
    'Upload Featured Image': 'رفع الصورة الرئيسية',
    'Additional Images': 'صور إضافية',
    'Upload Images': 'رفع الصور',
    'Publish News': 'نشر الخبر',
    'Publishing...': 'جاري النشر...',
    'News Published Successfully!': 'تم نشر الخبر بنجاح!',
    'Your news has been published successfully.': 'تم نشر خبرك بنجاح.',
    'Please fill all required fields': 'يرجى ملء جميع الحقول المطلوبة',
    'Please add a featured image': 'يرجى إضافة صورة رئيسية',
    'Error saving news:': 'خطأ في حفظ الخبر:',
    'Upload widget is still loading. Please try again.': 'أداة التحميل لا تزال تحمّل. يرجى المحاولة مرة أخرى.',
    'Breaking News': 'خبر عاجل',
    'Official Statement': 'بيان رسمي',
    'Field Report': 'تقرير ميداني',
    'Update': 'تحديث',
    'Analysis': 'تحليل'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  const toggleLanguage = () => {
    setIsArabic(!isArabic);
  };

  // MODIFIED: Always use Arabic font for both languages
  const getFontFamily = (weight = 400) => {
    return `'ITF Qomra Arabic', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif`;
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => setWidgetLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const openCloudinaryWidget = (isFeatured = false) => {
    if (!widgetLoaded) {
      setError(translateText("Upload widget is still loading. Please try again."));
      return;
    }

    window.cloudinary.openUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'drl2kb984',
        uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'salafcore',
        sources: ['local', 'url', 'camera'],
        multiple: !isFeatured,
        clientAllowedFormats: ['jpg', 'jpeg', 'png'],
        maxImageFileSize: 5000000,
        maxFiles: isFeatured ? 1 : 5
      },
      (error, result) => {
        if (!error && result && result.event === 'success') {
          if (isFeatured) {
            setFeaturedImage(result.info.secure_url);
          } else {
            setNewsImages(prev => [...prev, result.info.secure_url]);
          }
        }
      }
    );
  };

  const removeImage = (index, isFeatured = false) => {
    if (isFeatured) {
      setFeaturedImage('');
    } else {
      setNewsImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.title || !formData.content || !formData.category || !formData.author) {
      setError(translateText('Please fill all required fields'));
      setLoading(false);
      return;
    }

    if (!featuredImage) {
      setError(translateText('Please add a featured image'));
      setLoading(false);
      return;
    }

    try {
      const token = uuidv4();

      const newsData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        featuredImage,
        additionalImages: newsImages,
        token: token,
        status: 'published',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        views: 0,
        likes: 0,
        isNews: true
      };

      await addDoc(collection(db1, 'news'), newsData);

      await setDoc(doc(db1, 'news_tokens', token), {
        title: formData.title,
        createdAt: serverTimestamp(),
        status: 'published',
        type: 'news'
      });

      setSuccess(true);
    } catch (err) {
      setError(`${translateText('Error saving news:')} ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #F0F0F0 0%, #FFFFFF 50%, #F0F0F0 100%)',
      padding: '0',
      fontFamily: getFontFamily(),
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh',
      color: '#32373B'
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      padding: '3rem 2rem',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      backdropFilter: 'blur(5px)',
      border: '1px solid #D3BA80',
      marginBottom: '3rem',
      marginTop: '2rem'
    },
    header: {
      textAlign: 'center',
      color: '#32373B',
      padding: '120px 20px 40px',
      background: `linear-gradient(135deg, 
        rgba(211, 186, 128, 0.1) 0%, 
        rgba(211, 186, 128, 0.2) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden',
      borderBottom: '3px solid #D3BA80'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      color: '#255853',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      letterSpacing: '-0.5px',
      lineHeight: '1.2',
      position: 'relative',
      fontFamily: getFontFamily(700)
    },
    titleAccent: {
      display: 'block',
      width: '80px',
      height: '4px',
      background: 'linear-gradient(90deg, #D3BA80, #E0CFA6)',
      margin: '1rem auto',
      borderRadius: '2px'
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#255853',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 88, 83, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontFamily: getFontFamily(500)
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(211, 186, 128, 0.08)',
      borderRadius: '50%'
    },
    footerSection: {
      backgroundColor: '#255853',
      color: '#FFFFFF',
      padding: '3rem 2rem',
      marginTop: '4rem',
      textAlign: 'center',
      fontFamily: getFontFamily(500)
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.floatingShapes}>
        <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(211, 186, 128, 0.05)'}}></div>
        <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(53, 126, 119, 0.08)'}}></div>
        <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(224, 207, 166, 0.06)'}}></div>
      </div>

      <div style={styles.languageToggle}>
        <button 
          onClick={toggleLanguage}
          style={styles.toggleButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#D3BA80';
            e.target.style.transform = 'translateY(-3px) scale(1.05)';
            e.target.style.boxShadow = '0 8px 20px rgba(211, 186, 128, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#255853';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(37, 88, 83, 0.3)';
          }}
        >
          {isArabic ? 'ENGLISH' : 'العربية'}
        </button>
      </div>

      <header style={styles.header}>
        <h1 style={styles.title}>{translateText('News Upload')}</h1>
        <div style={styles.titleAccent}></div>
        <p style={{ color: '#357E77', fontSize: '1.2rem', fontFamily: getFontFamily(500) }}>
          {translateText('Publish your news article')}
        </p>
      </header>

      <div style={styles.content}>
        <Card className="shadow-lg" style={{
          border: '1px solid #E0CFA6',
          borderRadius: '12px',
          backgroundColor: '#FFFFFF'
        }}>
          <Card.Body style={{ padding: '30px' }}>
            <div className="text-center mb-4">
              <h2 style={{ color: '#255853', fontWeight: '700', fontFamily: getFontFamily(700) }}>
                {translateText('News Upload')}
              </h2>
              <p style={{ color: '#5d6d7e', fontFamily: getFontFamily(400) }}>
                {translateText('Publish your news article')}
              </p>
            </div>

            {success ? (
              <Alert variant="light" style={{ 
                border: '1px solid #D3BA80',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                color: '#255853',
                fontFamily: getFontFamily(400)
              }}>
                <div className="d-flex align-items-center">
                  <div className="rounded p-2 me-3" style={{ 
                    backgroundColor: '#255853' 
                  }}>
                    <i className="bi bi-check-circle-fill text-light fs-4"></i>
                  </div>
                  <div>
                    <h4 style={{ color: '#255853', fontFamily: getFontFamily(700) }}>{translateText('News Published Successfully!')}</h4>
                    <p style={{ color: '#255853', fontFamily: getFontFamily(400) }}>{translateText('Your news has been published successfully.')}</p>
                  </div>
                </div>
              </Alert>
            ) : (
              <Form onSubmit={handleSubmit}>
                {error && (
                  <Alert variant="light" style={{ 
                    border: '1px solid #D3BA80',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    color: '#255853',
                    fontFamily: getFontFamily(400)
                  }}>
                    <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#D3BA80' }}></i>
                    {error}
                  </Alert>
                )}

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#255853', fontWeight: '600', fontFamily: getFontFamily(600) }}>{translateText('Title *')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    placeholder={translateText('Enter news title')}
                    style={{ 
                      border: '1px solid rgba(53, 126, 119, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      color: '#32373B',
                      fontFamily: getFontFamily(400)
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#255853', fontWeight: '600', fontFamily: getFontFamily(600) }}>{translateText('Content *')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    name="content"
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    required
                    placeholder={translateText('Write your news content')}
                    style={{ 
                      border: '1px solid rgba(53, 126, 119, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      resize: 'none',
                      color: '#32373B',
                      fontFamily: getFontFamily(400)
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#255853', fontWeight: '600', fontFamily: getFontFamily(600) }}>{translateText('Category *')}</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                    style={{ 
                      border: '1px solid rgba(53, 126, 119, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      color: '#32373B',
                      fontFamily: getFontFamily(400)
                    }}
                  >
                    <option value="news">{translateText('Breaking News')}</option>
                    <option value="official">{translateText('Official Statement')}</option>
                    <option value="field_report">{translateText('Field Report')}</option>
                    <option value="update">{translateText('Update')}</option>
                    <option value="analysis">{translateText('Analysis')}</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#255853', fontWeight: '600', fontFamily: getFontFamily(600) }}>{translateText('Author *')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    required
                    placeholder={translateText('Enter author name')}
                    style={{ 
                      border: '1px solid rgba(53, 126, 119, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      color: '#32373B',
                      fontFamily: getFontFamily(400)
                    }}
                  />
                  <Form.Text style={{ color: '#7f8c8d', fontFamily: getFontFamily(400) }}>
                    {isArabic ? 'المؤلف الافتراضي: مؤسسة إرضاء الإعلامية' : 'Default author: مؤسسة إرضاء الإعلامية'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#255853', fontWeight: '600', fontFamily: getFontFamily(600) }}>{translateText('Excerpt')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                    placeholder={translateText('Brief description of the news')}
                    style={{ 
                      border: '1px solid rgba(53, 126, 119, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      resize: 'none',
                      color: '#32373B',
                      fontFamily: getFontFamily(400)
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#255853', fontWeight: '600', fontFamily: getFontFamily(600) }}>{translateText('Tags')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    placeholder={translateText('Comma-separated tags')}
                    style={{ 
                      border: '1px solid rgba(53, 126, 119, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      color: '#32373B',
                      fontFamily: getFontFamily(400)
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#255853', fontWeight: '600', fontFamily: getFontFamily(600) }}>{translateText('Featured Image *')}</Form.Label>
                  <div className="mb-3">
                    <Button 
                      variant="outline-dark" 
                      onClick={() => openCloudinaryWidget(true)}
                      type="button"
                      className="d-flex align-items-center me-2"
                      style={{ 
                        color: '#255853', 
                        borderColor: '#255853',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontFamily: getFontFamily(500)
                      }}
                    >
                      <i className="bi bi-cloud-arrow-up-fill me-2"></i>
                      {translateText('Upload Featured Image')}
                    </Button>
                  </div>
                  
                  {featuredImage && (
                    <div className="position-relative" style={{ maxWidth: '300px' }}>
                      <Image 
                        src={featuredImage} 
                        thumbnail
                        style={{ 
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover', 
                          border: '1px solid rgba(53, 126, 119, 0.3)',
                          borderRadius: '8px'
                        }}
                      />
                      <Button
                        variant="light"
                        size="sm"
                        className="position-absolute top-0 end-0 p-0"
                        style={{ 
                          width: '24px', 
                          height: '24px',
                          borderRadius: '50%',
                          border: '1px solid rgba(53, 126, 119, 0.3)',
                          color: '#255853',
                          fontFamily: getFontFamily(500)
                        }}
                        onClick={() => removeImage(0, true)}
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#255853', fontWeight: '600', fontFamily: getFontFamily(600) }}>{translateText('Additional Images')}</Form.Label>
                  <div className="mb-3">
                    <Button 
                      variant="outline-dark" 
                      onClick={() => openCloudinaryWidget(false)}
                      type="button"
                      className="d-flex align-items-center"
                      style={{ 
                        color: '#255853', 
                        borderColor: '#255853',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontFamily: getFontFamily(500)
                      }}
                    >
                      <i className="bi bi-cloud-arrow-up-fill me-2"></i>
                      {translateText('Upload Images')}
                    </Button>
                  </div>
                  
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    {newsImages.map((img, index) => (
                      <div key={index} className="position-relative" style={{ width: '120px' }}>
                        <Image 
                          src={img} 
                          thumbnail
                          style={{ 
                            height: '120px', 
                            objectFit: 'cover', 
                            border: '1px solid rgba(53, 126, 119, 0.3)',
                            borderRadius: '8px'
                          }}
                        />
                        <Button
                          variant="light"
                          size="sm"
                          className="position-absolute top-0 end-0 p-0"
                          style={{ 
                            width: '24px', 
                            height: '24px',
                            borderRadius: '50%',
                            border: '1px solid rgba(53, 126, 119, 0.3)',
                            color: '#255853',
                            fontFamily: getFontFamily(500)
                          }}
                          onClick={() => removeImage(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={loading}
                  className="w-100 py-3"
                  style={{ 
                    backgroundColor: '#255853', 
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontFamily: getFontFamily(600)
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner as="span" size="sm" animation="border" className="me-2" />
                      {translateText('Publishing...')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send-fill me-2"></i>
                      {translateText('Publish News')}
                    </>
                  )}
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      </div>

      <footer style={styles.footerSection}>
        <Container>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            © 2024 {isArabic ? 'مؤسسة إرضاء الإعلامية' : 'مؤسسة إرضاء الإعلامية'}. {isArabic ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default NewsUploadPage;